// Lokale Spielstände in IndexedDB: Autosave + drei manuelle Plätze.
import type { GameState } from '../../shared/logic.ts';

const DB = 'die-letzte-runde';
const STORE = 'saves';

export type SlotId = 'auto' | 'slot1' | 'slot2' | 'slot3';
export const SLOTS: SlotId[] = ['auto', 'slot1', 'slot2', 'slot3'];

/** Spielstände je Kapitel getrennt (Schlüssel „kapitel:platz“) */
let prefix = '';
export function setSaveChapter(id: string) {
  prefix = id + ':';
}

export interface SaveGame {
  slot: SlotId;
  state: GameState;
  savedAt: number;
  sceneName: string;
  thumb?: string;
}

let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  if (!dbp) {
    dbp = new Promise((res, rej) => {
      const r = indexedDB.open(DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: 'slot' });
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  }
  return dbp;
}

function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return db().then(
    (d) =>
      new Promise<T>((res, rej) => {
        const t = d.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => res(req.result);
        req.onerror = () => rej(req.error);
      }),
  );
}

export async function writeSave(s: SaveGame): Promise<void> {
  try {
    await tx('readwrite', (st) => st.put(JSON.parse(JSON.stringify({ ...s, slot: prefix + s.slot }))));
    dispatchEvent(new Event('dlr-save-written'));
  } catch (e) {
    console.warn('Speichern fehlgeschlagen', e);
  }
}

export async function readSave(slot: SlotId): Promise<SaveGame | null> {
  try {
    const r = (await tx<SaveGame | undefined>('readonly', (st) => st.get(prefix + slot))) ?? null;
    return r ? { ...r, slot } : null;
  } catch {
    return null;
  }
}

export async function listSaves(): Promise<SaveGame[]> {
  try {
    const all = await tx<SaveGame[]>('readonly', (st) => st.getAll());
    return all.filter((x) => String(x.slot).startsWith(prefix)).map((x) => ({ ...x, slot: String(x.slot).slice(prefix.length) as SlotId }));
  } catch {
    return [];
  }
}

export async function deleteSave(slot: SlotId): Promise<void> {
  try {
    await tx('readwrite', (st) => st.delete(prefix + slot));
  } catch {
    /* egal */
  }
}

/** alle Spielstände aller Kapitel (für den Cloud-Spielstand) */
export async function allSavesRaw(): Promise<unknown[]> {
  try {
    return await tx<unknown[]>('readonly', (st) => st.getAll());
  } catch {
    return [];
  }
}

/** Spielstände aus der Cloud übernehmen (überschreibt gleiche Plätze) */
export async function putSavesRaw(list: unknown[]): Promise<void> {
  for (const s of list) {
    try {
      await tx('readwrite', (st) => st.put(s as object));
    } catch {
      /* einzelner Stand kaputt: überspringen */
    }
  }
}
